import { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import VertretungSelect from '../components/VertretungSelect';
import { useI18n } from '../i18n';

export default function RequestsView() {
  const now = new Date();
  const { t, locale } = useI18n();
  const { hasPermission, user } = useAuth();
  const canApprove = hasPermission('approve_vacation');
  const [entitlement, setEntitlement] = useState(null);
  const [requests, setRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const emptyForm = { request_type: 'URLAUB', start_date: '', end_date: '', notes: '', substitute_id: '' };
  const [form, setForm] = useState(emptyForm);
  const [kandidaten, setKandidaten] = useState([]);
  const [pflicht, setPflicht] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveForm, setMoveForm] = useState({ start_date: '', end_date: '' });
  const isFzt = form.request_type === 'FZT';

  useEffect(() => {
    api.get('/teams/').then((res) => {
      setPflicht(res.data.some((tm) => tm.vertretung_erforderlich && tm.members.some((m) => m.user_id === user?.id)));
    }).catch(() => {});
    api.get('/company-settings/').then((res) => setCompanySettings(res.data)).catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (!showForm || !form.start_date || !form.end_date || form.end_date < form.start_date) {
      setKandidaten([]);
      return;
    }
    api.get('/teams/vertretung/vorschlaege', { params: { start_date: form.start_date, end_date: form.end_date } })
      .then((res) => setKandidaten(res.data))
      .catch(() => setKandidaten([]));
  }, [showForm, form.start_date, form.end_date]);

  const load = async () => {
    setLoading(true);
    try {
      const calls = [
        api.get(`/vacation/entitlement?year=${now.getFullYear()}`),
        api.get('/vacation/requests'),
      ];
      if (canApprove) calls.push(api.get('/vacation/requests?all_users=true'));
      const results = await Promise.all(calls);
      setEntitlement(results[0].data);
      setRequests(results[1].data);
      if (canApprove) setAllRequests(results[2].data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/vacation/request', { ...form, substitute_id: form.substitute_id ? Number(form.substitute_id) : null });
      setShowForm(false);
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t('vacation.error.submit'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (id) => {
    const reason = window.prompt(t('vacation.prompt.approve')) ?? '';
    try {
      await api.put(`/vacation/requests/${id}/approve`, { reason: reason || null });
    } catch (err) {
      window.alert(err.response?.data?.detail || t('vacation.error.approve'));
    }
    await load();
  };

  const handleReject = async (id) => {
    const reason = window.prompt(t('vacation.prompt.reject'));
    if (!reason || !reason.trim()) return;
    try {
      await api.put(`/vacation/requests/${id}/reject`, { reason });
    } catch (err) {
      window.alert(err.response?.data?.detail || t('vacation.error.reject'));
    }
    await load();
  };

  const handleDelete = async (r) => {
    const name = r.user_name || t('vacation.user.fallback', { id: r.user_id });
    const from = new Date(r.start_date).toLocaleDateString(locale);
    const to = new Date(r.end_date).toLocaleDateString(locale);
    if (!window.confirm(t('vacation.delete.confirm', { name, from, to }))) return;
    setActionError('');
    try {
      await api.delete(`/vacation/requests/${r.id}`);
    } catch (err) {
      setActionError(err.response?.data?.detail || t('vacation.delete.error'));
    }
    await load();
  };

  const openMove = (r) => {
    setActionError('');
    setMoveTarget(r);
    setMoveForm({ start_date: r.start_date, end_date: r.end_date });
  };

  const handleMove = async (e) => {
    e.preventDefault();
    setActionError('');
    try {
      await api.put(`/vacation/requests/${moveTarget.id}/move`, moveForm);
      setMoveTarget(null);
      await load();
    } catch (err) {
      setActionError(err.response?.data?.detail || t('vacation.move.error'));
    }
  };

  const typeBadge = (type) => (
    <span className={`badge ${type === 'FZT' ? 'bg-info text-dark' : 'bg-primary'}`}>{t(`requests.type.${type || 'URLAUB'}`)}</span>
  );

  // Genommene und offene FZT-Tage des laufenden Jahres aus den eigenen Anträgen.
  const fztUsed = requests
    .filter((r) => r.request_type === 'FZT' && r.status !== 'REJECTED' && new Date(r.start_date).getFullYear() === now.getFullYear())
    .reduce((sum, r) => sum + r.days_count, 0);
  const fztMax = companySettings?.comp_time_max_days_per_user || 0;
  const fztEnabled = !!companySettings?.comp_time_enabled;

  const statusBadge = (status) => {
    const map = { PENDING: 'bg-warning text-dark', APPROVED: 'bg-success', REJECTED: 'bg-danger' };
    return <span className={`badge ${map[status]}`}>{t(`vacation.status.${status}`)}</span>;
  };

  if (loading) return <div className="text-center"><div className="spinner-border" /></div>;

  const total = entitlement?.total_days || 0;
  const usedPct = total ? Math.round((entitlement.used_days / total) * 100) : 0;
  const plannedPct = total ? Math.round((entitlement.planned_days / total) * 100) : 0;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h4 className="mb-0"><i className="bi bi-send me-2" />{t('requests.title')}</h4>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          <i className="bi bi-plus-circle me-2" />{t('requests.new')}
        </button>
      </div>

      {fztEnabled && (
        <div className="card mb-4">
          <div className="card-body">
            <h6 className="card-title">{t('requests.fzt.quota', { year: now.getFullYear() })}</h6>
            <div className="row g-3">
              {[
                { label: t('requests.fzt.total'), val: fztMax, color: 'text-primary' },
                { label: t('requests.fzt.used'), val: fztUsed, color: 'text-warning' },
                { label: t('requests.fzt.available'), val: Math.max(fztMax - fztUsed, 0), color: 'text-success' },
              ].map(({ label, val, color }) => (
                <div className="col-4" key={label}>
                  <div className="text-center">
                    <div className={`fs-4 fw-bold ${color}`}>{val}</div>
                    <div className="text-muted small">{label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {entitlement && (
        <div className="card mb-4">
          <div className="card-body">
            <h6 className="card-title">{t('vacation.entitlement', { year: now.getFullYear() })}</h6>
            <div className="row g-3 mb-3">
              {[
                { label: t('vacation.total'), val: entitlement.total_days, color: 'text-primary' },
                { label: t('vacation.used'), val: entitlement.used_days, color: 'text-danger' },
                { label: t('vacation.planned'), val: entitlement.planned_days, color: 'text-warning' },
                { label: t('vacation.available'), val: entitlement.remaining_days, color: 'text-success' },
              ].map(({ label, val, color }) => (
                <div className="col-6 col-md-3" key={label}>
                  <div className="text-center">
                    <div className={`fs-4 fw-bold ${color}`}>{val}</div>
                    <div className="text-muted small">{label}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="progress" style={{ height: 20 }}>
              <div className="progress-bar bg-danger" style={{ width: `${usedPct}%` }} title={t('vacation.used.title', { days: entitlement.used_days })} />
              <div className="progress-bar bg-warning" style={{ width: `${plannedPct}%` }} title={t('vacation.planned.title', { days: entitlement.planned_days })} />
              <div className="progress-bar bg-success opacity-25" style={{ width: `${100 - usedPct - plannedPct}%` }} />
            </div>
            <div className="d-flex gap-3 mt-2 small text-muted">
              <span><span className="badge bg-danger me-1">              &nbsp;</span>{t('vacation.used')}</span>
                            <span><span className="badge bg-warning me-1">&nbsp;</span>{t('vacation.planned')}</span>
                            <span><span className="badge bg-success opacity-50 me-1">&nbsp;</span>{t('vacation.available')}</span>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="card mb-4">
          <div className="card-body">
            <h6 className="card-title">{isFzt ? t('requests.form.titleFzt') : t('vacation.form.title')}</h6>
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-12">
                  <label className="form-label">{t('requests.form.type')}</label>
                  <select className="form-select" value={form.request_type}
                    onChange={(e) => setForm({ ...form, request_type: e.target.value })}>
                    <option value="URLAUB">{t('requests.type.URLAUB')}</option>
                    {fztEnabled && <option value="FZT">{t('requests.type.FZT')}</option>}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">{t('vacation.form.from')}</label>
                  <input type="date" className="form-control" required
                    value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">{t('vacation.form.to')}</label>
                  <input type="date" className="form-control" required
                    value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">{t('vacation.form.notes')}</label>
                  <input type="text" className="form-control"
                    value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">{pflicht ? t('vacation.form.substituteRequired') : t('vacation.form.substituteOptional')}</label>
                  <select className="form-select" required={pflicht} value={form.substitute_id}
                    onChange={(e) => setForm({ ...form, substitute_id: e.target.value })}>
                    <option value="">{t('vacation.substitute.pick')}</option>
                    {kandidaten.map((k) => (
                      <option key={k.user_id} value={k.user_id} disabled={!k.verfuegbar}>
                        {k.verfuegbar ? k.name : t('vacation.substitute.unavailable', { name: k.name, reason: k.grund })}
                      </option>
                    ))}
                  </select>
                  {pflicht && <small className="text-muted">{isFzt ? t('requests.form.substituteMandatoryHintFzt') : t('vacation.form.substituteMandatoryHint')}</small>}
                  {kandidaten.length === 0 && form.start_date && form.end_date && (
                    <small className="text-muted d-block">{t('vacation.substitute.none')}</small>
                  )}
                </div>
              </div>
              {error && <div className="alert alert-danger mt-2 py-2">{error}</div>}
              <div className="mt-3 d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting && <span className="spinner-border spinner-border-sm me-2" />}{t('vacation.form.submit')}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowForm(false)}>{t('vacation.form.cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header"><strong>{t('requests.my.title')}</strong></div>
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>{t('requests.col.type')}</th><th>{t('vacation.col.from')}</th><th>{t('vacation.col.to')}</th><th>{t('vacation.col.days')}</th><th>{t('vacation.col.status')}</th><th>{t('vacation.col.substitute')}</th><th>{t('vacation.col.notes')}</th><th>{t('vacation.col.reason')}</th><th>{t('vacation.col.submitted')}</th>
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 ? (
                <tr><td colSpan={9} className="text-center text-muted py-4">{t('vacation.empty')}</td></tr>
              ) : requests.map((r) => (
                <tr key={r.id}>
                  <td>{typeBadge(r.request_type)}</td>
                  <td>{new Date(r.start_date).toLocaleDateString(locale)}</td>
                  <td>{new Date(r.end_date).toLocaleDateString(locale)}</td>
                  <td>{r.days_count}</td>
                  <td>{statusBadge(r.status)}</td>
                  <td><VertretungSelect request={r} onChanged={load} /></td>
                  <td><small className="text-muted">{r.notes || '–'}</small></td>
                  <td><small className="text-muted">{r.decision_reason || '–'}</small></td>
                  <td><small>{r.created_at ? new Date(r.created_at).toLocaleDateString(locale) : '–'}</small></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {canApprove && (
        <div className="card mt-4">
          <div className="card-header d-flex align-items-center gap-2">
            <i className="bi bi-check2-circle text-success" />
            <strong>{t('requests.team.title')}</strong>
            <span className="badge bg-warning text-dark ms-auto">
              {t('vacation.team.open', { count: allRequests.filter((r) => r.status === 'PENDING').length })}
            </span>
          </div>
          {actionError && !moveTarget && <div className="alert alert-danger m-3 py-2">{actionError}</div>}
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr><th>{t('vacation.col.employee')}</th><th>{t('requests.col.type')}</th><th>{t('vacation.col.from')}</th><th>{t('vacation.col.to')}</th><th>{t('vacation.col.days')}</th><th>{t('vacation.col.status')}</th><th>{t('vacation.col.substitute')}</th><th>{t('vacation.col.notes')}</th><th>{t('vacation.col.reason')}</th><th>{t('vacation.col.actions')}</th></tr>
              </thead>
              <tbody>
                {allRequests.length === 0 ? (
                  <tr><td colSpan={10} className="text-center text-muted py-4">{t('vacation.empty')}</td></tr>
                ) : allRequests.map((r) => (
                  <tr key={r.id}>
                    <td><small>{r.user_name || t('vacation.user.fallback', { id: r.user_id })}</small></td>
                    <td>{typeBadge(r.request_type)}</td>
                    <td>{new Date(r.start_date).toLocaleDateString(locale)}</td>
                    <td>{new Date(r.end_date).toLocaleDateString(locale)}</td>
                    <td>{r.days_count}</td>
                    <td>{statusBadge(r.status)}</td>
                    <td><small>{r.substitute_name || '–'}</small></td>
                    <td><small className="text-muted">{r.notes || '–'}</small></td>
                    <td><small className="text-muted">{r.decision_reason || '–'}</small></td>
                    <td>
                      {r.status === 'PENDING' && (
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-success" onClick={() => handleApprove(r.id)} title={t('vacation.approve')}><i className="bi bi-check-lg" /></button>
                          <button className="btn btn-outline-danger" onClick={() => handleReject(r.id)} title={t('vacation.reject')}><i className="bi bi-x-lg" /></button>
                        </div>
                      )}
                      {r.status === 'APPROVED' && (
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-primary" onClick={() => openMove(r)} title={t('vacation.move')}><i className="bi bi-calendar-event" /></button>
                          <button className="btn btn-outline-danger" onClick={() => handleDelete(r)} title={t('vacation.delete')}><i className="bi bi-trash" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {moveTarget && (
        <>
          <div className="modal-backdrop fade show" />
          <div className="modal fade show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog">
              <form className="modal-content" onSubmit={handleMove}>
                <div className="modal-header">
                  <h5 className="modal-title">{t('vacation.move.title')}</h5>
                  <button type="button" className="btn-close" onClick={() => setMoveTarget(null)} />
                </div>
                <div className="modal-body">
                  <p className="text-muted">{t('vacation.move.hint', { name: moveTarget.user_name || t('vacation.user.fallback', { id: moveTarget.user_id }) })}</p>
                  <div className="row g-3">
                    <div className="col-6">
                      <label className="form-label">{t('vacation.form.from')}</label>
                      <input type="date" className="form-control" required value={moveForm.start_date}
                        onChange={(e) => setMoveForm({ ...moveForm, start_date: e.target.value })} />
                    </div>
                    <div className="col-6">
                      <label className="form-label">{t('vacation.form.to')}</label>
                      <input type="date" className="form-control" required value={moveForm.end_date}
                        onChange={(e) => setMoveForm({ ...moveForm, end_date: e.target.value })} />
                    </div>
                  </div>
                  {actionError && <div className="alert alert-danger mt-3 py-2 mb-0">{actionError}</div>}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setMoveTarget(null)}>{t('vacation.form.cancel')}</button>
                  <button type="submit" className="btn btn-primary">{t('vacation.move.save')}</button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
